import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Modal, Nav, OverlayTrigger, Row, Spinner, Tooltip } from "react-bootstrap";
import { FaBell, FaCheckCircle, FaClock, FaComments, FaFileAlt, FaFolder, FaFolderOpen, FaListUl, FaQuestionCircle, FaSyncAlt, FaUsers } from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import { acknowledgeOobEvent, confirmOobSecondaryIdentity, downloadOobParticipantEventArtifact, getOobParticipantBoard, getOobParticipantEventArtifacts, getOobParticipantEventQuestions, getOobParticipantLocalEventChannelMessages, getOobParticipantLocalEventChannels, OobEventArtifactDto, OobEventQuestionDto, OobLocalEventChannelDto, OobLocalEventMessageDto, OobParticipantBoardDto, postOobParticipantLocalEventChannelMessage, submitOobParticipantEventQuestion } from "@/api/outOfBandApi";
import { useOobSession } from "@/contexts/OobSessionContext";
import { formatDuration, OobDeliveryStatusBanner, OobEventMetricStrip, OobEventWorkspaceHeader } from "@/components/Oob/OobEventWorkspace";

const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : "-");
const fmtRemaining = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m ${seconds}s`;
};

const OobStatusBoard: React.FC = () => {
  const { eventGuid } = useParams<{ eventGuid: string }>();
  const navigate = useNavigate();
  const { session, isSessionActive, clearSession } = useOobSession();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmingIdentity, setConfirmingIdentity] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [board, setBoard] = useState<OobParticipantBoardDto | null>(null);
  const [artifacts, setArtifacts] = useState<OobEventArtifactDto[]>([]);
  const [questions, setQuestions] = useState<OobEventQuestionDto[]>([]);
  const [questionText, setQuestionText] = useState("");
  const [questionAnonymous, setQuestionAnonymous] = useState(true);
  const [submittingQuestion, setSubmittingQuestion] = useState(false);
  const [localSpaceTab, setLocalSpaceTab] = useState<string>("updates");
  const [localChannels, setLocalChannels] = useState<OobLocalEventChannelDto[]>([]);
  const [selectedChannelGuid, setSelectedChannelGuid] = useState<string>("");
  const [channelMessages, setChannelMessages] = useState<OobLocalEventMessageDto[]>([]);
  const [refreshingChannel, setRefreshingChannel] = useState(false);
  const [channelMessageBody, setChannelMessageBody] = useState("");
  const [postingChannelMessage, setPostingChannelMessage] = useState(false);
  const [comment, setComment] = useState("");
  const [sessionWarning, setSessionWarning] = useState<string | null>(null);
  const [sessionRemainingMs, setSessionRemainingMs] = useState<number | null>(null);
  const [fileViewMode, setFileViewMode] = useState<"folders" | "list">("folders");
  const [fileSearch, setFileSearch] = useState("");
  const [expandedFileFolders, setExpandedFileFolders] = useState<Set<string>>(new Set());

  // Check OOB session validity on mount and set up timeout warning
  useEffect(() => {
    if (session && !isSessionActive()) {
      setError("Your session has expired. Please authenticate again.");
      clearSession();
      navigate("/external");
      return;
    }

    // Check session expiration periodically and show progressively stronger warnings.
    const checkSessionTimer = setInterval(() => {
      if (session?.sessionExpiresAt) {
        const expiresAt = new Date(session.sessionExpiresAt);
        const now = new Date();
        const remainingMs = expiresAt.getTime() - now.getTime();
        const minutesRemaining = Math.floor(remainingMs / 60000);

        setSessionRemainingMs(Math.max(0, remainingMs));

        if (minutesRemaining <= 0) {
          setError("Your session has expired. Please authenticate again.");
          clearSession();
          navigate("/external");
        } else if (minutesRemaining <= 1) {
          setSessionWarning("Your session expires in under a minute. Save your work now.");
        } else if (minutesRemaining <= 5 && minutesRemaining > 0) {
          setSessionWarning(`Your session will expire in ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''}.`);
        } else if (minutesRemaining <= 15) {
          setSessionWarning(`Session active. ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''} remaining.`);
        } else {
          setSessionWarning(null);
        }
      }
    }, 30000);

    // Prime immediately so timer appears without waiting for first interval tick.
    if (session?.sessionExpiresAt) {
      const expiresAt = new Date(session.sessionExpiresAt);
      setSessionRemainingMs(Math.max(0, expiresAt.getTime() - Date.now()));
    }

    return () => clearInterval(checkSessionTimer);
  }, [session, isSessionActive, clearSession, navigate]);

  const load = async () => {
    if (!eventGuid) {
      setError("Missing event ID.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    console.log("Loading OOB board for event:", eventGuid);
    try {
      const data = await getOobParticipantBoard(eventGuid);
      console.log("Board data received:", data);
      setBoard(data);
      setArtifacts(await getOobParticipantEventArtifacts(eventGuid));
      setQuestions(await getOobParticipantEventQuestions(eventGuid));
      if (data.event?.deliveryStatus === "LocalActive") {
        const channels = await getOobParticipantLocalEventChannels(eventGuid);
        setLocalChannels(channels);
        setSelectedChannelGuid((current) => current && channels.some((channel) => channel.channelGuid === current) ? current : (channels[0]?.channelGuid || ""));
      } else {
        setLocalChannels([]);
        setSelectedChannelGuid("");
        setChannelMessages([]);
      }
    } catch (err: any) {
      console.error("Error loading board:", err);
      
      // If 401 and we have OOB session, it expired
      if (err?.response?.status === 401 && session?.sessionToken) {
        clearSession();
        navigate("/external");
        return;
      }

      const errorMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to load status board.";
      console.error("Error message:", errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventGuid]);

  const handleAcknowledge = async () => {
    if (!eventGuid || !board?.event) return;

    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      // Backend will get user email from authenticated claims or OOB session
      await acknowledgeOobEvent(eventGuid, {
        participantEmail: "", // Backend uses User.Identity?.Name from claims or OOB session
        ackSource: "Portal",
        optionalComment: comment || undefined,
      });
      setSuccess("Acknowledgement submitted.");
      await load();
    } catch (err: any) {
      console.error("Error submitting acknowledgement:", err);
      
      // If 401 and we have OOB session, it expired
      if (err?.response?.status === 401 && session?.sessionToken) {
        clearSession();
        navigate("/external");
        return;
      }

      setError(err?.response?.data?.error || err?.message || "Failed to submit acknowledgement.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmSecondaryIdentity = async () => {
    if (!eventGuid) {
      setError("Event could not be identified.");
      return;
    }

    try {
      setConfirmingIdentity(true);
      setError(null);
      await confirmOobSecondaryIdentity(eventGuid);
      setSuccess("Secondary Tenant access confirmed. Your OOB identity is ready for future events.");
      const refreshed = await getOobParticipantBoard(eventGuid);
      setBoard(refreshed);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Complete the Secondary Tenant access test before confirming.");
    } finally {
      setConfirmingIdentity(false);
    }
  };

  const handleDownloadArtifact = async (artifact: OobEventArtifactDto) => {
    if (!eventGuid) return;

    try {
      setError(null);
      await downloadOobParticipantEventArtifact(eventGuid, artifact.artifactGuid, artifact.artifactName);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to download event file.");
    }
  };

  const handleSubmitQuestion = async () => {
    if (!eventGuid || !questionText.trim()) return;
    try {
      setSubmittingQuestion(true);
      setError(null);
      await submitOobParticipantEventQuestion(eventGuid, { questionText: questionText.trim(), displayAsAnonymous: questionAnonymous });
      setQuestionText("");
      setSuccess("Question submitted to the OOB administration team.");
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to submit your question.");
    } finally {
      setSubmittingQuestion(false);
    }
  };

  const loadChannelMessages = async (channelGuid: string) => {
    if (!eventGuid || !channelGuid) return;
    try {
      setChannelMessages(await getOobParticipantLocalEventChannelMessages(eventGuid, channelGuid));
    } catch (err: any) {
      setChannelMessages([]);
      setError(err?.response?.data?.error || err?.message || "Failed to load channel messages.");
    }
  };

  const handleSelectChannel = async (channelGuid: string) => {
    setSelectedChannelGuid(channelGuid);
    await loadChannelMessages(channelGuid);
  };

  const handleRefreshChannel = async () => {
    if (!eventGuid || !selectedChannelGuid) return;
    setRefreshingChannel(true);
    try {
      const [messages, channels] = await Promise.all([
        getOobParticipantLocalEventChannelMessages(eventGuid, selectedChannelGuid),
        getOobParticipantLocalEventChannels(eventGuid),
      ]);
      setChannelMessages(messages);
      setLocalChannels(channels);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to refresh channel messages.");
    } finally {
      setRefreshingChannel(false);
    }
  };

  const handleOpenChannel = async (channelGuid?: string) => {
    const generalChannel = localChannels.find((channel) => channel.channelKey === "general") || localChannels[0];
    const targetChannel = channelGuid ? localChannels.find((channel) => channel.channelGuid === channelGuid) : generalChannel;
    setLocalSpaceTab("channels");
    if (targetChannel) await handleSelectChannel(targetChannel.channelGuid);
  };

  const handlePostChannelMessage = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!eventGuid || !selectedChannelGuid || !channelMessageBody.trim()) return;
    try {
      setPostingChannelMessage(true);
      await postOobParticipantLocalEventChannelMessage(eventGuid, selectedChannelGuid, { messageBody: channelMessageBody.trim() });
      setChannelMessageBody("");
      await loadChannelMessages(selectedChannelGuid);
      setLocalChannels(await getOobParticipantLocalEventChannels(eventGuid));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "You cannot post in this channel.");
    } finally {
      setPostingChannelMessage(false);
    }
  };

  useEffect(() => {
    if (selectedChannelGuid) void loadChannelMessages(selectedChannelGuid);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannelGuid]);

  const filteredArtifacts = useMemo(() => {
    const term = fileSearch.trim().toLowerCase();
    return term ? artifacts.filter((artifact) => artifact.artifactName.toLowerCase().includes(term)) : artifacts;
  }, [artifacts, fileSearch]);

  const artifactFolders = useMemo(() => {
    const map = new Map<string, OobEventArtifactDto[]>();
    filteredArtifacts.forEach((artifact) => {
      const key = artifact.artifactType || "Other";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(artifact);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredArtifacts]);

  useEffect(() => {
    setExpandedFileFolders(new Set(artifacts.map((artifact) => artifact.artifactType || "Other")));
  }, [artifacts]);

  const toggleFileFolder = (folder: string) => {
    setExpandedFileFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folder)) next.delete(folder); else next.add(folder);
      return next;
    });
  };

  const pinnedUpdates = board?.event?.updates.filter((u) => u.isPinned) ?? [];
  const startedDt = board?.event?.activatedDt || board?.event?.createDt;

  return (
    <Container fluid className="px-4 py-3 oob-participant-page">
      {error && <Alert variant="danger" className="mb-3" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" className="mb-3" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}
      {sessionWarning && <Alert variant="warning" className="mb-3"><strong>Session expiring:</strong> {sessionWarning}</Alert>}
      
      {!loading && board?.event && (
        <>
          <OobDeliveryStatusBanner event={board.event} />
          <OobEventWorkspaceHeader
            event={board.event}
            participantCount={board.includedParticipantCount}
            compact
            actions={sessionRemainingMs !== null ? (
              <span className={`oob-event-status-chip oob-event-status-chip--${sessionRemainingMs <= 5 * 60000 ? "pending" : "info"}`}>Session {fmtRemaining(sessionRemainingMs)}</span>
            ) : undefined}
          />
          <OobEventMetricStrip compact metrics={[
            { label: "Updates", value: board.event.updates.length, icon: FaClock },
            { label: "Members", value: board.includedParticipantCount, icon: FaUsers },
            { label: "Acknowledged", value: board.acknowledgedParticipantCount, icon: FaCheckCircle },
            { label: "Files", value: artifacts.length, icon: FaFileAlt },
            { label: "Duration", value: formatDuration(startedDt), icon: FaClock },
          ]} />
          {board.event.deliveryStatus === "LocalActive" && (
            <Row className="g-3 oob-participant-workspace">
              <Col lg={3}>
                <Nav variant="pills" className="flex-column oob-participant-workspace__nav" aria-label="Event sections">
                  <Nav.Item><OverlayTrigger placement="right" overlay={<Tooltip>Official announcements and instructions from OOB administrators.</Tooltip>}><Nav.Link active={localSpaceTab === "updates"} onClick={() => setLocalSpaceTab("updates")}><FaBell /><span>Event Updates ({board.event.updates.length})</span></Nav.Link></OverlayTrigger></Nav.Item>
                  <Nav.Item><OverlayTrigger placement="right" overlay={<Tooltip>Local event channels for messages and files.</Tooltip>}><Nav.Link active={localSpaceTab === "channels"} onClick={() => void handleOpenChannel()}><FaComments /><span>Channels ({localChannels.reduce((sum, channel) => sum + channel.messageCount, 0)})</span></Nav.Link></OverlayTrigger></Nav.Item>
                  {localChannels.map((channel) => <Nav.Item key={channel.channelGuid} className="oob-participant-workspace__subtab"><Nav.Link active={localSpaceTab === "channels" && selectedChannelGuid === channel.channelGuid} onClick={() => void handleOpenChannel(channel.channelGuid)}><FaComments /><span>{channel.displayName} ({channel.messageCount})</span></Nav.Link></Nav.Item>)}
                  <Nav.Item><OverlayTrigger placement="right" overlay={<Tooltip>Approved questions and answers, plus a private question submission form for OOB administrators.</Tooltip>}><Nav.Link active={localSpaceTab === "questions"} onClick={() => setLocalSpaceTab("questions")}><FaQuestionCircle /><span>Questions and Answers ({questions.length})</span></Nav.Link></OverlayTrigger></Nav.Item>
                  <Nav.Item><Nav.Link active={localSpaceTab === "files"} onClick={() => setLocalSpaceTab("files")}><FaFileAlt /><span>Files ({artifacts.length})</span></Nav.Link></Nav.Item>
                </Nav>
              </Col>
              <Col lg={6}>
                <div className="oob-participant-workspace__content">
          {localSpaceTab === "channels" && (
            <Card className="oob-local-channel-participant">
              <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                <div className="d-flex align-items-center gap-2"><FaComments /><strong>{localChannels.find((channel) => channel.channelGuid === selectedChannelGuid)?.displayName || "Local Event Channel"}</strong></div>
                <Button size="sm" variant="outline-secondary" onClick={() => void handleRefreshChannel()} disabled={refreshingChannel || !selectedChannelGuid} aria-label="Refresh channel messages" title="Refresh channel messages">
                  <FaSyncAlt className={refreshingChannel ? "oob-refresh-icon--spinning" : ""} />
                </Button>
              </Card.Header>
              <Card.Body>
                {localChannels.length === 0 ? <div className="text-muted">No local channels are available yet.</div> : (
                  <div className="d-flex flex-column gap-2">
                    {(() => {
                      const selectedChannel = localChannels.find((channel) => channel.channelGuid === selectedChannelGuid);
                      return selectedChannel && <>
                        <div className="small text-muted">{selectedChannel.description}</div>
                        <div className="oob-local-channel-messages">
                          {channelMessages.length === 0 ? <div className="text-muted text-center py-3">No messages yet.</div> : channelMessages.map((message) => {
                            const isOwnMessage = Boolean(session?.participantGuid && message.participantGuid === session.participantGuid);
                            return (
                              <div key={message.messageGuid} className={`oob-local-channel-message-row ${isOwnMessage ? "oob-local-channel-message-row--own" : ""}`}>
                                <div className={`oob-local-channel-message ${isOwnMessage ? "oob-local-channel-message--own" : ""}`}>
                                  <div className="oob-local-channel-message__meta">
                                    <strong>{message.authorDisplayName || message.authorEmail}</strong>
                                    <time dateTime={message.createDt}>{fmt(message.createDt)}</time>
                                  </div>
                                  <div className="oob-local-channel-message__body">{message.messageBody}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {selectedChannel.postingPolicy === "Participants" && (
                          board.event.status === "Active" ? (
                            <Form onSubmit={handlePostChannelMessage}><Form.Group><Form.Label className="visually-hidden">New channel message</Form.Label><Form.Control as="textarea" rows={3} placeholder={`Post to ${selectedChannel.displayName}`} value={channelMessageBody} onChange={(event) => setChannelMessageBody(event.target.value)} maxLength={4000} /></Form.Group><div className="d-flex justify-content-end mt-2"><Button type="submit" size="sm" disabled={postingChannelMessage || !channelMessageBody.trim()}>{postingChannelMessage ? "Posting..." : "Post Message"}</Button></div></Form>
                          ) : (
                            <div className="oob-channel-closed-notice small text-muted">This event is closed and no longer accepting messages. Contact the OOB administrators with any questions.</div>
                          )
                        )}
                      </>;
                    })()}
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          {localSpaceTab === "updates" && (
          <Card style={{ opacity: board.hasAcknowledged ? 1 : 0.6 }}>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Pinned / Latest Instructions</strong>
            </Card.Header>
            <Card.Body>
              {board.event.updates.length === 0 ? (
                <div className="text-muted">No updates posted yet.</div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {board.event.updates.map((u) => (
                    <Card key={u.updateGuid} border={u.isPinned ? "danger" : "light"}>
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="fw-semibold">{u.messageTitle}</div>
                          <div className="d-flex gap-2">
                            {u.isPinned && <span className="oob-event-status-chip oob-event-status-chip--error">Pinned</span>}
                            <span className={`oob-event-status-chip oob-event-status-chip--${u.importance === "Critical" ? "error" : u.importance === "Important" ? "pending" : "info"}`}>{u.importance}</span>
                          </div>
                        </div>
                        <div className="mt-2">{u.messageBody}</div>
                        {u.linkUrl && (
                          <div className="mt-1">
                            <a href={u.linkUrl} target="_blank" rel="noopener noreferrer">
                              {u.linkLabel || u.linkUrl}
                            </a>
                          </div>
                        )}
                        <div className="small text-muted mt-2">Posted {fmt(u.createDt)}</div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
          )}

          {localSpaceTab === "files" && (
            <Card>
              <Card.Header className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                <div className="d-flex align-items-center gap-2">
                  <FaFileAlt aria-hidden="true" />
                  <strong>Event Files</strong>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Form.Control size="sm" type="search" placeholder="Search files" value={fileSearch} onChange={(event) => setFileSearch(event.target.value)} style={{ maxWidth: 200 }} />
                  <div className="btn-group btn-group-sm" role="group" aria-label="File view mode">
                    <Button variant={fileViewMode === "folders" ? "primary" : "outline-secondary"} onClick={() => setFileViewMode("folders")}><FaFolder className="me-1" aria-hidden="true" />Folders</Button>
                    <Button variant={fileViewMode === "list" ? "primary" : "outline-secondary"} onClick={() => setFileViewMode("list")}><FaListUl className="me-1" aria-hidden="true" />List</Button>
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                {filteredArtifacts.length === 0 ? (
                  <div className="text-muted">{fileSearch ? "No files match your search." : "No event files have been published yet."}</div>
                ) : fileViewMode === "list" ? (
                  <div className="d-flex flex-column gap-2">
                    {filteredArtifacts.map((artifact) => (
                      <div key={artifact.artifactGuid} className="d-flex justify-content-between align-items-center gap-3 border-bottom pb-2">
                        <div className="min-w-0">
                          <div className="fw-semibold text-break">{artifact.artifactName}</div>
                          <div className="small text-muted">{artifact.artifactType} · {artifact.provisioningStatus}</div>
                        </div>
                        <Button size="sm" variant="outline-primary" onClick={() => handleDownloadArtifact(artifact)}>
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="oob-file-tree">
                    {artifactFolders.map(([folder, files]) => {
                      const isExpanded = expandedFileFolders.has(folder);
                      return (
                        <div key={folder} className="oob-file-tree__folder">
                          <button type="button" className="oob-file-tree__folder-toggle" onClick={() => toggleFileFolder(folder)} aria-expanded={isExpanded}>
                            {isExpanded ? <FaFolderOpen aria-hidden="true" /> : <FaFolder aria-hidden="true" />}
                            <span>{folder}</span>
                            <span className="oob-file-tree__count">{files.length}</span>
                          </button>
                          {isExpanded && (
                            <div className="oob-file-tree__files">
                              {files.map((artifact) => (
                                <div key={artifact.artifactGuid} className="oob-file-tree__file">
                                  <div className="min-w-0">
                                    <div className="fw-semibold text-break">{artifact.artifactName}</div>
                                    <div className="small text-muted">{artifact.provisioningStatus}</div>
                                  </div>
                                  <Button size="sm" variant="outline-primary" onClick={() => handleDownloadArtifact(artifact)}>
                                    Download
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          {localSpaceTab === "questions" && (
            <Card>
              <Card.Header className="d-flex align-items-center gap-2">
                <FaQuestionCircle aria-hidden="true" />
                <strong>Questions and Answers</strong>
              </Card.Header>
              <Card.Body>
                {questions.length > 0 && (
                  <div className="d-flex flex-column gap-2 mb-4">
                    {questions.map((question) => (
                      <div key={question.questionGuid} className="border-bottom pb-2">
                        <div className="fw-semibold">{question.questionText}</div>
                        <div className="mt-1">{question.answerText}</div>
                      </div>
                    ))}
                  </div>
                )}
                <Form onSubmit={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  void handleSubmitQuestion();
                }}>
                  <Form.Group className="mb-2">
                    <Form.Label>Ask the OOB administration team</Form.Label>
                    <Form.Control as="textarea" rows={3} value={questionText} onChange={(event) => setQuestionText(event.target.value)} maxLength={2000} required />
                  </Form.Group>
                  <Form.Check className="mb-3" type="checkbox" id="oob-question-anonymous" label="Display my question anonymously if it is published" checked={questionAnonymous} onChange={(event) => setQuestionAnonymous(event.target.checked)} />
                  <Button type="submit" variant="outline-primary" disabled={submittingQuestion || !questionText.trim()}>
                    {submittingQuestion ? "Submitting..." : "Submit Question"}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          )}
                </div>
              </Col>
              <Col lg={3} className="oob-participant-workspace__aside">
                <Card className="mb-3 oob-participant-event-info">
                  <Card.Header>Event Information</Card.Header>
                  <Card.Body>
                    <div className="small text-muted mb-1">Status</div><div className="mb-3">{board.event.status}</div>
                    <div className="small text-muted mb-1">Communication</div><div className="mb-3">Local communication space</div>
                    <div className="small text-muted mb-1">Started</div><div>{fmt(startedDt)}</div>
                  </Card.Body>
                </Card>
                <Card className={`mb-3 oob-participant-ack ${board.hasAcknowledged ? "oob-participant-ack--confirmed" : "oob-participant-ack--pending"}`}>
                  <Card.Header>Acknowledgement</Card.Header>
                  <Card.Body>
                    <span className={`oob-event-status-chip oob-event-status-chip--${board.hasAcknowledged ? "confirmed" : "pending"}`}>{board.hasAcknowledged ? "Confirmed" : "Awaiting acknowledgement"}</span>
                    {board.hasAcknowledged ? <div className="small text-muted mt-2">Confirmed {fmt(board.lastAcknowledgedDt)}</div> : <div className="small text-muted mt-2">Review the event instructions, then acknowledge this event.</div>}
                  </Card.Body>
                </Card>
                {board.event.eventType === "Onboarding" && (
                  <Card className="mb-3"><Card.Header>Backup Identity</Card.Header><Card.Body className="small">Complete the backup-identity access test, then confirm it here.<Button className="mt-2 w-100" size="sm" variant="outline-primary" onClick={handleConfirmSecondaryIdentity} disabled={confirmingIdentity}>{confirmingIdentity ? "Confirming..." : "Confirm Access"}</Button></Card.Body></Card>
                )}
                <Card>
                  <Card.Header>Quick Actions</Card.Header>
                  <Card.Body className="py-1">
                    <button type="button" className="oob-event-quick-link" onClick={() => setLocalSpaceTab("files")}><span><FaFileAlt className="me-2" />View Files</span><span>›</span></button>
                    <button type="button" className="oob-event-quick-link" onClick={() => setLocalSpaceTab("questions")}><span><FaQuestionCircle className="me-2" />Ask a Question</span><span>›</span></button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {board.event.deliveryStatus !== "LocalActive" && (
            <Card style={{ opacity: board.hasAcknowledged ? 1 : 0.6 }}>
              <Card.Header><strong>Pinned / Latest Instructions</strong></Card.Header>
              <Card.Body>{board.event.updates.length === 0 ? <div className="text-muted">No updates posted yet.</div> : board.event.updates.map((update) => <div key={update.updateGuid} className="border-bottom py-2"><div className="fw-semibold">{update.messageTitle}</div><div className="mt-1">{update.messageBody}</div></div>)}</Card.Body>
            </Card>
          )}

          <Modal
            show={!board.hasAcknowledged}
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
            fullscreen="sm-down"
          >
            <Modal.Header>
              <Modal.Title>Confirm Event Acknowledgement</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="mb-3">Please confirm that you received and understood this activation notice.</p>

              <div className="mb-3 p-3 bg-light border rounded">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <div className="fw-semibold">{board.event.eventName}</div>
                    <span className={`oob-event-status-chip oob-event-status-chip--${board.event.status === "Active" ? "error" : board.event.status === "Closed" ? "info" : "pending"}`}>{board.event.status}</span>
                  </div>
                  <div className="small text-muted">{board.event.eventType}</div>
                </div>
                {board.event.description && <div className="small mb-2">{board.event.description}</div>}
              </div>

              {pinnedUpdates.length > 0 && (
                <div className="mb-3">
                  <div className="fw-semibold mb-2">Pinned instructions</div>
                  <div className="d-flex flex-column gap-2">
                    {pinnedUpdates.map((u) => (
                      <Card key={u.updateGuid} border="light">
                        <Card.Body>
                          <div className="fw-semibold">{u.messageTitle}</div>
                          <div className="small mt-1">{u.messageBody}</div>
                          {u.linkUrl && (
                            <div className="small mt-1">
                              <a href={u.linkUrl} target="_blank" rel="noopener noreferrer">
                                {u.linkLabel || u.linkUrl}
                              </a>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              <Form.Group>
                <Form.Label>Optional comment</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="E.g., I am ready and standing by"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onClick={handleAcknowledge} disabled={submitting}>
                {submitting ? "Submitting..." : "Acknowledge Event"}
              </Button>
            </Modal.Footer>
          </Modal>

          {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
          {success && <Alert variant="success" className="mt-3">{success}</Alert>}
        </>
      )}

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <div className="mt-2">Loading status board...</div>
        </div>
      )}

      {!loading && !board?.event && !error && (
        <Alert variant="warning">No event data available.</Alert>
      )}
    </Container>
  );
};

export default OobStatusBoard;
